import axiosInstance, { axiosInstanceFast } from "./axiosInstance";
import { getCookie } from "@utils/utils";

// Fetch saved search data
export const fetchSavedSearchData = async () => {
  const payload = { tab: "process" };
  const response = await axiosInstance.post("/getShadowEngineData/", payload, {
    headers: {
      "X-CSRFToken": getCookie("csrftoken"),
      "Content-Type": "multipart/form-data",
    },
  });

  if (response.data.msg === "success") {
    return response.data.data.savedSearches;
  } else {
    throw new Error("Failed to fetch saved searches");
  }
};

// Delete a saved search
export const deleteSavedSearch = async (searchName: string) => {
  const payload = {
    searchName,
    savedSearch: "1",
  };

  const response = await axiosInstance.post(
    "/delete-shadow360-search/",
    payload,
    {
      headers: {
        "X-CSRFToken": getCookie("csrftoken"),
        "Content-Type": "multipart/form-data",
      },
    },
  );

  if (response.data.status === "success") {
    return response.data;
  } else {
    throw new Error("Failed to delete saved search");
  }
};

// Toggle the status of a saved search
export const toggleSavedSearchStatus = async ({
  searchName,
  index,
  newStatus,
}: {
  searchName: string;
  index: number;
  newStatus: "active" | "inactive";
}) => {
  const response = await axiosInstance.post(
    "/disableEnableSSearch/",
    { SSname: searchName },
    {
      headers: {
        "X-CSRFToken": getCookie("csrftoken"),
        "Content-Type": "multipart/form-data",
      },
    },
  );

  if (response.data.status === "success") {
    return { ...response.data, index, newStatus }; // pass back index and newStatus to access in onSuccess
  } else {
    throw new Error(response.data.msg || "Failed to toggle status");
  }
};

// Import saved searches
export const importSavedSearches = async ({
  file,
  replaceExisting,
}: {
  file: File;
  replaceExisting: boolean;
}) => {
  const csrfToken = getCookie("csrftoken");

  if (!csrfToken) {
    throw new Error("CSRF token is missing.");
  }

  const formData = new FormData();
  formData.append("importSavedSearchFile", file);
  formData.append("replaceExistingSS", replaceExisting.toString());

  const response = await axiosInstance.post(
    "/import-saved-searches/",
    formData,
    {
      headers: {
        "X-CSRFToken": csrfToken,
      },
    },
  );

  if (response.data?.msg === "failure") {
    const errorDetail = response.data?.data || "Unknown error occurred.";

    if (
      typeof errorDetail === "string" &&
      errorDetail.toLowerCase().includes("product type mismatch")
    ) {
      throw new Error("Product type mismatch. Please check the file format.");
    }

    throw new Error(`Failed to import saved searches: ${errorDetail}`);
  }

  return response.data;
};

// Fetch data list
export const fetchDataList = async () => {
  const payload = { tab: "process" };

  const response = await axiosInstance.post("/getShadowEngineData/", payload, {
    headers: {
      "X-CSRFToken": getCookie("csrftoken"),
      "Content-Type": "multipart/form-data",
    },
  });

  if (response.data.msg === "success") {
    return response.data.data.dataList;
  } else {
    throw new Error("Failed to fetch data");
  }
};

// Function to handle CSV download
export const downloadCSV = async (dlname: string) => {
  try {
    const response = await axiosInstance.post("/downloadDatalistCSV/", {
      dlname,
    });

    if (response.status === 200 && response.data?.data) {
      const filePath = response.data.data;

      // Fetch the file using GET request as a blob
      const fileResponse = await axiosInstance.get(filePath, {
        responseType: "blob",
      });

      // Trigger download
      const blob = new Blob([fileResponse.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${dlname}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } else {
      console.error("Unexpected response:", response.data);
      throw new Error("Couldn't find the requested data list CSV.");
    }
  } catch (error) {
    console.error("Error downloading CSV:", error);
    throw new Error("Couldn't find the requested data list CSV.");
  }
};

// Fetch datalist preview
export const viewDatalistPreview = async ({
  dlName,
  idx = 0,
  searchedVal,
  column,
}: {
  dlName: string;
  idx?: number;
  searchedVal?: string;
  column?: string;
}) => {
  try {
    const response = await axiosInstance.post(
      "/viewDatalistPreview/",
      { dlName, idx, searchedVal, column },
      {
        headers: {
          "X-CSRFToken": getCookie("csrftoken"),
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data) {
      return response.data;
    } else {
      throw new Error("Error fetching datalist details");
    }
  } catch (error) {
    console.error("Error in API call:", error);
    throw new Error("Error fetching datalist details");
  }
};

export const editDatalist = async (dlName: string) => {
  return viewDatalistPreview({ dlName, idx: 0 });
};

export const searchedDlFull = async ({
  searchedVal,
  dlName,
  column,
}: {
  searchedVal: string;
  dlName: string;
  column?: string;
}) => {
  try {
    const response = await axiosInstance.post(
      "/searchedDlFull/",
      { searchedVal, dlName, column },
      {
        headers: {
          "X-CSRFToken": getCookie("csrftoken"),
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data) {
      return response.data;
    }

    throw new Error("Error searching datalist details");
  } catch (error) {
    throw new Error("Error searching datalist details");
  }
};

// delete datalist
export const deleteDatalist = async ({
  dlName,
  dlType,
}: {
  dlName: string;
  dlType: string;
}) => {
  const response = await axiosInstance.post(
    "/deleteDatalist/",
    { dlName, dlType },
    {
      headers: {
        "X-CSRFToken": getCookie("csrftoken"),
        "Content-Type": "multipart/form-data",
      },
    },
  );

  if (response.data.status === "success") {
    return response.data.response;
  } else {
    throw new Error(response.data.msg || "Unknown error");
  }
};

// Function to fetch log mapping fields data
export const fetchLogMappingFields = async () => {
  const response = await axiosInstance.post(
    "/get-all-log-mappings/",
    {},
    {
      headers: {
        "X-CSRFToken": getCookie("csrftoken"),
      },
    },
  );

  if (response.data.msg === "success") {
    return response.data.data;
  } else {
    throw new Error("Failed to fetch log mapping fields");
  }
};

// api call for creating datalist
export const createDatalist = async (data: any) => {
  const response = await axiosInstance.post("/createDatalist/", data, {
    headers: {
      "X-CSRFToken": getCookie("csrftoken"),
      "Content-Type": "multipart/form-data",
    },
  });

  const result = response.data;

  if (result.status === "success") {
    return result;
  } else if (result.status === "failure") {
    throw new Error(result.msg);
  } else {
    throw new Error("Something went wrong!");
  }
};

// api call to add a new row to the datalist
export const addRow = async ({
  data,
  dlName,
}: {
  data: any;
  dlName: string;
}) => {
  const response = await axiosInstance.post(
    "/addDatalistNewRow/",
    {
      data: JSON.stringify(data),
      uid: dlName,
    },
    {
      headers: {
        "X-CSRFToken": getCookie("csrftoken"),
        "Content-Type": "multipart/form-data", // Adjust content type if needed
      },
    },
  );

  if (response.data.status === "success") {
    return response.data;
  } else {
    throw new Error(
      response.data.msg || "An error occurred while adding the row.",
    );
  }
};

// API function to delete rows from a datalist
export const deleteRow = async ({
  selRows,
  dlName,
  type,
}: {
  selRows: any[];
  dlName: string;
  type: string;
}) => {
  const formData = new FormData();

  formData.append("data", JSON.stringify(selRows));

  const allColumnNames = Array.from(
    new Set(
      selRows.reduce<string[]>((acc, row) => {
        Object.keys(row).forEach((key) => acc.push(key));
        return acc;
      }, []),
    ),
  );

  formData.append("columns", JSON.stringify(allColumnNames));
  formData.append("uid", dlName);
  formData.append("listType", type);

  const response = await axiosInstance.post("/deleteDatalistRow/", formData, {
    headers: {
      "X-CSRFToken": getCookie("csrftoken"),
    },
  });

  if (response.data.status !== "success") {
    throw new Error(response.data.msg || "Failed to delete rows");
  }

  return response.data;
};

// API function to upload CSV
export const uploadCSV = async ({
  csvFile,
  dlName,
}: {
  csvFile: File;
  dlName: string;
}) => {
  const formData = new FormData();
  formData.append("csvList", csvFile);
  formData.append("replaceExistingDlRecords", "true");
  formData.append("dataListName", dlName);

  const response = await axiosInstance.post(
    "/uploadAdditionalListCsv/",
    formData,
    {
      headers: {
        "X-CSRFToken": getCookie("csrftoken"),
        "Content-Type": "multipart/form-data",
      },
    },
  );

  if (response.data.status === "success") {
    return response.data;
  } else {
    throw new Error(response.data.message || "Error uploading the file");
  }
};

// API function to update the datalist description
export const updateDatalistDescription = async ({
  dataListDesc,
  dlName,
}: {
  dataListDesc: string;
  dlName: string;
}) => {
  const response = await axiosInstance.post(
    "/editDataListDesc/",
    {
      dataListDesc,
      dlname: dlName,
    },
    {
      headers: {
        "X-CSRFToken": getCookie("csrftoken"),
        "Content-Type": "multipart/form-data",
      },
    },
  );

  if (response.data.status === "success") {
    return response.data;
  } else {
    throw new Error(
      response.data.msg || "Failed to update the datalist description",
    );
  }
};

// upload csv list api call
export const uploadCsvList = async ({
  csvFile,
  uploadForm,
  columns,
}: {
  csvFile: File;
  uploadForm: {
    name: string;
    description: string;
    firstRowContainsColumnNames: string;
    columnTypes: { [key: string]: string };
  };
  columns: string[];
}) => {
  const csrfToken = getCookie("csrftoken");

  if (!csrfToken) throw new Error("CSRF Token is missing.");

  const formData = new FormData();
  formData.append("csrfmiddlewaretoken", csrfToken);
  formData.append("listName", uploadForm.name);
  formData.append("listDesc", uploadForm.description);
  formData.append("hasColumnNames", uploadForm.firstRowContainsColumnNames);
  formData.append("csvList", csvFile);

  columns.forEach((column, index) => {
    if (uploadForm.firstRowContainsColumnNames == "yes") {
      formData.append(`csvColumnName${index}`, column);
    } else {
      formData.append(`csvColumnName${index}`, `Column${index + 1}`);
    }
    formData.append(
      `csvColumnType${index}`,
      uploadForm.columnTypes[column] || "plain_text",
    );
  });

  const response = await axiosInstance.post("/uploadListCsv/", formData, {
    headers: {
      "X-CSRFToken": csrfToken,
    },
  });

  if (response.data.message === "Success") {
    return response.data;
  } else {
    throw new Error(response.data.ex || "Something went wrong.");
  }
};

// Fetch data sources and transform them into the desired format
export const fetchDataSources = async () => {
  const response = await axiosInstance.get("/shadow360GetSearchList/", {
    headers: {
      "X-CSRFToken": getCookie("csrftoken"),
      "Content-Type": "application/json",
    },
  });

  if (response.data) {
    return Array.isArray(response.data)
      ? response.data.map((item: any) => ({
          label: item.list_name,
          value: item.list_name,
        }))
      : [];
  } else {
    throw new Error("Failed to fetch data sources.");
  }
};

// Preview data source
export const previewDataSource = async (dataSource: string) => {
  const csrfToken = getCookie("csrftoken");

  const formData = new FormData();
  formData.append("dataSource", dataSource);

  const response = await axiosInstance.post(
    "/ai-engine/dataset/view/",
    formData,
    {
      headers: {
        "X-CSRFToken": csrfToken!,
      },
    },
  );

  if (response.data.data?.length > 0) {
    return response.data;
  } else {
    throw new Error("No. of columns should be greater than 1.");
  }
};

// API function to analyze the dataset
export const analyseDataset = async ({
  formData,
  tableCols,
  variableTypes,
  generateFeaturesAuto,
}: {
  formData: {
    name: string;
    description: string;
    dataSource: string;
  };
  tableCols: string[];
  variableTypes: { [key: string]: string };
  generateFeaturesAuto: number;
}) => {
  const csrfToken = getCookie("csrftoken");

  const filledVariableTypes = tableCols.reduce((acc: any, column: string) => {
    acc[`variableType___${column}`] =
      variableTypes[`variableType___${column}`] || "categorical";
    return acc;
  }, {});

  const payload = {
    modelName: formData.name,
    data_source: formData.dataSource,
    modelDesc: formData.description,
    ...(generateFeaturesAuto === 1 ? { generateFeaturesAuto } : {}),
    ...filledVariableTypes,
  };

  const response = await axiosInstance.post(
    "/ai-engine/dataset/analyse/",
    payload,
    {
      headers: {
        "X-CSRFToken": csrfToken!,
      },
    },
  );

  if (response.status === 200) {
    return response.data;
  } else {
    throw new Error("Something went wrong. Please try again.");
  }
};

// API function to predict target feature
export const predictTargetFeature = async ({
  dataSource,
  columnName,
}: {
  dataSource: string;
  columnName: string;
}) => {
  const csrfToken = getCookie("csrftoken");

  const payload = {
    data_source: dataSource,
    column_name: columnName,
    isDatetime: false,
  };

  const response = await axiosInstance.post(
    "/ai-engine/dataset/predict/",
    payload,
    {
      headers: {
        "X-CSRFToken": csrfToken!,
      },
    },
  );

  if (response.data) {
    return response.data;
  } else {
    throw new Error("Failed to fetch prediction data.");
  }
};

// API function to train the model
export const trainModel = async ({
  target,
  classification,
  dataSource,
  modelName,
  partitioning,
  modelRun,
  cv,
  holdOut,
  validationPercentage,
  includedFields,
}: {
  target: string;
  classification: string;
  dataSource: string;
  modelName: string;
  partitioning: string;
  modelRun: string;
  cv: number;
  holdOut: string;
  validationPercentage: number;
  includedFields: string[];
}) => {
  const csrfToken = getCookie("csrftoken");

  const payload = {
    targetField: target,
    targetClassification: classification,
    isDatetime: false,
    dsrc: dataSource,
    modelName: modelName,
    partitioningMethod: partitioning,
    modelRunMethod: modelRun,
    cvFoldVal: cv,
    holdoutRangeVal: holdOut,
    validationPercentage: validationPercentage,
    includedFields: includedFields,
  };

  const response = await axiosInstance.post(
    "/ai-engine/model/train/",
    payload,
    {
      headers: {
        "X-CSRFToken": csrfToken!,
      },
    },
  );

  if (response.data) {
    return response.data;
  } else {
    throw new Error("Failed to train model.");
  }
};

// API function to fetch models
export const fetchModels = async () => {
  const csrfToken = getCookie("csrftoken");

  const payload = { tab: "process" };

  const response = await axiosInstance.post("/getShadowEngineData/", payload, {
    headers: {
      "X-CSRFToken": csrfToken!,
      "Content-Type": "multipart/form-data",
    },
  });

  if (response.data.msg === "success") {
    return response.data.data.models;
  } else {
    throw new Error("Failed to fetch models.");
  }
};

// API call to delete a model
export const deleteModel = async (modelName: string) => {
  const payload = { modelName };
  const response = await axiosInstance.post("/deleteModelSE/", payload, {
    headers: {
      "X-CSRFToken": getCookie("csrftoken"),
      "Content-Type": "multipart/form-data",
    },
  });

  if (response.status !== 200) {
    throw new Error("Failed to delete model");
  }
  return response.data.response.message;
};

// API function to fetch models Details
export const fetchMlModelsDetails = async (item: string) => {
  const payload = { item };
  const response = await axiosInstance.post("/getModelPlotsData/", payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  if (response.status !== 200) {
    throw new Error("Failed to delete model");
  }
  return response.data;
};

export const getDataSettings = async (signal?: AbortSignal) => {
  const payload = {};
  const response = await axiosInstanceFast.post(
    "/rdd/backend/v1/get-data-settings",
    payload,
    { signal },
  );
  return response.data;
};

export const uploadDocument = async (file: File, category: string) => {
  const csrfToken = getCookie("csrftoken");

  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", category);

  const response = await axiosInstanceFast.post(
    "/rdd/backend/v1/settings-document-upload",
    formData,
    {
      headers: {
        "X-CSRFToken": csrfToken,
      },
      transformRequest: (data) => data,
    },
  );

  return response.data;
};

export const deleteDocument = async (index: number) => {
  const csrfToken = getCookie("csrftoken");

  const payload = {
    upload_classify_doc: {
      index: index,
    },
  };

  const response = await axiosInstanceFast.post(
    "/rdd/backend/v1/update-data-settings?widget=upload_classify_doc&operation=delete",
    payload,
    {
      headers: {
        "X-CSRFToken": csrfToken,
      },
    },
  );

  return response.data;
};
